import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { addDoc, collection, doc, getDocs, query, updateDoc, where } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminTeacherPrepView.css';
import './StudentDetailPages.css';
import StatCards from './StatCards';
import { scopeForAdmin, schoolMatches } from '../utils/schoolScope';

function SuperAdminTeacherPrepView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('' ) = national view
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || null }), [adminData, saSchoolId]);
  const [lessonPlans, setLessonPlans] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showDetail, setShowDetail] = useState(false);

  const [newPlan, setNewPlan] = useState({
    title: '',
    subject: '',
    classId: '',
    teacherId: '',
    description: '',
    dueDate: ''
  });

  const loadLessonPlans = useCallback(async () => {
    console.log('🔥 AdminTeacherPrep: Loading lesson plans');
    try {
      const plansData = [];
      for (const cls of classes) {
        const plansQuery = query(
          collection(db, 'classes', cls.id, 'teacherPreps'),
          where('isActive', '==', true)
        );
        const snapshot = await getDocs(plansQuery);
        const classPlans = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), classId: cls.id }));
        plansData.push(...classPlans);
      }
      console.log('🔥 AdminTeacherPrep: Lesson plans loaded', plansData.length, plansData);
      setLessonPlans(plansData);
    } catch (error) {
      console.error('🔥 AdminTeacherPrep: Error loading lesson plans:', error);
    } finally {
      setLoading(false);
    }
  }, [classes]);

  const loadTeachers = useCallback(async () => {
    console.log('🔥 AdminTeacherPrep: Loading teachers, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'users'));
      const teachersData = scopeForAdmin(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })), adminData).filter(u => u.role === 'Teacher');
      console.log('🔥 AdminTeacherPrep: Teachers loaded', teachersData.length, teachersData);
      setTeachers(teachersData);
    } catch (error) {
      console.error('🔥 AdminTeacherPrep: Error loading teachers:', error);
    }
  }, [adminData]);

  const loadClasses = useCallback(async () => {
    console.log('🔥 AdminTeacherPrep: Loading classes, schoolId:', adminData?.schoolId);
    try {
      const snapshot = await getDocs(collection(db, 'classes'));
      let classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      classesData = scopeForAdmin(classesData, adminData);
      console.log('🔥 AdminTeacherPrep: Classes loaded', classesData.length, classesData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminTeacherPrep: Error loading classes:', error);
    }
  }, [adminData]);

  useEffect(() => {
    loadTeachers();
    loadClasses();
  }, [loadTeachers, loadClasses]);

  useEffect(() => {
    if (classes.length > 0) {
      loadLessonPlans();
    }
  }, [classes, loadLessonPlans]);

  const handleAddPlan = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'classes', newPlan.classId, 'teacherPreps'), {
        ...newPlan,
        isActive: true,
        status: 'draft',
        createdAt: new Date().toISOString(),
        schoolId: adminData?.schoolId || null
      });
      setShowAddForm(false);
      setNewPlan({
        title: '',
        subject: '',
        classId: '',
        teacherId: '',
        description: '',
        dueDate: ''
      });
      loadLessonPlans();
    } catch (error) {
      console.error('Error adding lesson plan:', error);
      alert('Failed to add lesson plan');
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!confirm('Are you sure you want to delete this lesson plan?')) return;
    try {
      await updateDoc(doc(db, 'classes', plan.classId, 'teacherPreps', plan.id), { isActive: false });
      loadLessonPlans();
    } catch (error) {
      console.error('Error deleting lesson plan:', error);
      alert('Failed to delete lesson plan');
    }
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? teacher.username : 'Unassigned';
  };

  const getClassName = (classId) => {
    const cls = classes.find(c => c.id === classId);
    return cls ? cls.name : 'Unknown';
  };

  const getSchoolNameForClass = (classId) => {
    const cls = classes.find(c => c.id === classId);
    if (!cls || !cls.schoolId) return '';
    return saSchools.find(s => schoolMatches(s.id, cls.schoolId))?.name || '';
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (showDetail && selectedPlan) {
    const plan = selectedPlan;
    console.log('🔥 SuperAdminTeacherPrep: Opened plan detail', plan.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setShowDetail(false)}>◄ Back</button>
        <div className="detail-hero">
          <div className="detail-hero-icon">📚</div>
          <div>
            <h2 className="detail-page-title">
              {plan.title || plan.prepName}
              <span className={`detail-badge ${(plan.status || 'draft').toLowerCase()}`}>{plan.status || 'draft'}</span>
            </h2>
            <p className="detail-page-subtitle">
              {plan.subject || 'N/A'} · {getClassName(plan.classId)}
              {getSchoolNameForClass(plan.classId) ? ` · ${getSchoolNameForClass(plan.classId)}` : ''}
            </p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Lesson Plan Details</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">School</span><span className="detail-value">{getSchoolNameForClass(plan.classId) || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Subject</span><span className="detail-value">{plan.subject || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Class</span><span className="detail-value">{getClassName(plan.classId)}</span></div>
            <div className="detail-row"><span className="detail-label">Teacher</span><span className="detail-value">{getTeacherName(plan.teacherId)}</span></div>
            {plan.dueDate && (
              <div className="detail-row"><span className="detail-label">Due Date</span><span className="detail-value">{plan.dueDate}</span></div>
            )}
            {plan.createdAt && (
              <div className="detail-row"><span className="detail-label">Created</span><span className="detail-value">{new Date(plan.createdAt).toLocaleDateString()}</span></div>
            )}
          </div>
        </div>

        <div className="detail-card">
          <h4>Description</h4>
          {plan.description ? (
            <p className="detail-note">{plan.description}</p>
          ) : (
            <p className="detail-note" style={{ color: '#94a3b8' }}>No description provided</p>
          )}
        </div>

        {plan.subjectPlans && plan.subjectPlans.length > 0 && (
          <div className="detail-card">
            <h4>Subject Plans</h4>
            {plan.subjectPlans.map((sp, idx) => (
              <div key={idx} className="subject-plan-item">
                <h5>{sp.subjectName}</h5>
                {sp.planDetails && <p>{sp.planDetails}</p>}
                {sp.attachmentURLs && sp.attachmentURLs.length > 0 && (
                  <div className="attachment-preview-list">
                    {sp.attachmentURLs.map((url, aIdx) => {
                      const name = sp.attachmentNames?.[aIdx] || 'Attachment';
                      const isImage = url.match(/\.(jpg|jpeg|png|gif|webp|heic)/i);
                      return isImage ? (
                        <img key={aIdx} src={url} alt={name} className="attachment-image" />
                      ) : (
                        <a key={aIdx} href={url} target="_blank" rel="noopener noreferrer" className="attachment-pdf-link">
                          📄 {name}
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {plan.attachmentURLs && plan.attachmentURLs.length > 0 && (
          <div className="detail-card">
            <h4>Attachments</h4>
            <div className="attachment-preview-list">
              {plan.attachmentURLs.map((url, idx) => {
                const name = plan.attachmentNames?.[idx] || 'Attachment';
                const isImage = url.match(/\.(jpg|jpeg|png|gif|webp|heic)/i);
                return isImage ? (
                  <img key={idx} src={url} alt={name} className="attachment-image" />
                ) : (
                  <a key={idx} href={url} target="_blank" rel="noopener noreferrer" className="attachment-pdf-link">
                    📄 {name}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        <div className="detail-actions">
          <button
            className="btn-delete"
            onClick={() => {
              handleDeletePlan(plan);
              setShowDetail(false);
            }}
          >
            🗑️ Delete Lesson Plan
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-teacher-prep-view">
      <div className="view-header">
        <h2>Teacher Prep</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Lesson Plan
        </button>
      </div>
      <StatCards items={[['Total Preps',lessonPlans.length],['Classes',new Set(lessonPlans.map(p => p.classId).filter(Boolean)).size],['Submitted',teachers.filter(t => lessonPlans.some(p => p.teacherId === t.id)).length],['Never Submitted',teachers.filter(t => !lessonPlans.some(p => p.teacherId === t.id)).length],['Submission Rate',(teachers.length ? Math.round(teachers.filter(t => lessonPlans.some(p => p.teacherId === t.id)).length / teachers.length * 100) : 0) + '%'],['This Week',lessonPlans.filter(p => new Date(p.createdAt || p.date) >= new Date(Date.now() - 7 * 86400000)).length]]} />

      <p className="count-text">Lesson Plans: {lessonPlans.length}</p>

      {showAddForm && (
        <div className="add-plan-form">
          <h3>Create New Lesson Plan</h3>
          <form onSubmit={handleAddPlan}>
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={newPlan.title}
                onChange={(e) => setNewPlan({ ...newPlan, title: e.target.value })}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Subject *</label>
                <input
                  type="text"
                  value={newPlan.subject}
                  onChange={(e) => setNewPlan({ ...newPlan, subject: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Due Date *</label>
                <input
                  type="date"
                  value={newPlan.dueDate}
                  onChange={(e) => setNewPlan({ ...newPlan, dueDate: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Class *</label>
                <select
                  value={newPlan.classId}
                  onChange={(e) => setNewPlan({ ...newPlan, classId: e.target.value })}
                  required
                >
                  <option value="">Select Class</option>
                  {classes.map(cls => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name} ({cls.grade})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Teacher *</label>
                <select
                  value={newPlan.teacherId}
                  onChange={(e) => setNewPlan({ ...newPlan, teacherId: e.target.value })}
                  required
                >
                  <option value="">Select Teacher</option>
                  {teachers.map(teacher => (
                    <option key={teacher.id} value={teacher.id}>
                      {teacher.username}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newPlan.description}
                onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit">Create Plan</button>
            </div>
          </form>
        </div>
      )}

      {lessonPlans.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📚</div>
          <h3>No Lesson Plans</h3>
          <p>Create your first lesson plan to get started</p>
        </div>
      ) : (
        <div className="plans-list">
          {lessonPlans.map(plan => (
            <div
              key={plan.id}
              className="plan-card"
              onClick={() => {
                setSelectedPlan(plan);
                setShowDetail(true);
              }}
            >
              <div className="plan-icon">📚</div>
              <div className="plan-info">
                <h4>{plan.title || plan.prepName}</h4>
                <p>Subject: {plan.subject || 'N/A'}</p>
                <p>Class: {getClassName(plan.classId)}</p>
                {getSchoolNameForClass(plan.classId) && <p>🏫 {getSchoolNameForClass(plan.classId)}</p>}
                {plan.teacherId && <p>Teacher: {getTeacherName(plan.teacherId)}</p>}
                {plan.attachmentURLs && plan.attachmentURLs.length > 0 && (
                  <p className="attachment-count">📎 {plan.attachmentURLs.length} attachment{plan.attachmentURLs.length > 1 ? 's' : ''}</p>
                )}
              </div>
              <div className="plan-status">
                <span className="status-badge">{plan.status || 'draft'}</span>
              </div>
              <button
                className="delete-button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePlan(plan);
                }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}

    </div>
  );
}

export default SuperAdminTeacherPrepView;
